/* @jsx h */
import { defineComponent, Fragment, h, type VNodeChild } from "vue";
import { Dropdown } from "@lemmonhq/uikit";

type AccountMenuProps = {
  name: string;
  contextName: string;
  renderItems: () => VNodeChild;
  renderFooter?: () => VNodeChild;
  showHeaderDivider?: boolean;
};

export const AccountMenu = defineComponent<AccountMenuProps>({
  name: "AccountMenu",
  props: [
    "name",
    "contextName",
    "renderItems",
    "renderFooter",
    "showHeaderDivider",
  ],
  setup(props) {
    return () => {
      const firstName = props.name.split(" ")[0];
      const initials = props.name
        .split(" ")
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();

      return (
        <Dropdown
          placement="bottom-end"
          offset={8}
          closeOnContentClick
          contentClass={`menu w-56 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] !flex-nowrap rounded-xl border border-base-content/10 bg-base-300 p-1.5 shadow-xl ${
            props.renderFooter
              ? "overflow-hidden"
              : "overflow-x-hidden overflow-y-auto"
          }`}
        >
          {{
            trigger: () => (
              <div
                tabindex="0"
                role="button"
                class="flex cursor-pointer items-center gap-2 rounded-xl py-1 pl-1 pr-1.5 transition-colors duration-200 hover:bg-base-content/5"
              >
                <div class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-base-content/10 bg-base-content/10 text-[11px] font-semibold tracking-wide text-base-content/70">
                  {initials}
                </div>
                <div class="hidden min-w-0 text-left sm:block">
                  <div class="truncate text-[12px] font-medium leading-tight text-base-content/80">
                    Hey, {firstName}
                  </div>
                  <div class="max-w-[120px] truncate text-[10px] leading-tight text-base-content/35">
                    {props.contextName}
                  </div>
                </div>
                <span class="material-symbols-outlined hidden text-[16px] text-base-content/30 sm:block">
                  expand_more
                </span>
              </div>
            ),
            default: () => (
              <Fragment>
                <li class="pointer-events-none px-3 pb-1.5 pt-2 lg:hidden">
                  <div class="flex items-center gap-2.5 p-0 hover:bg-transparent">
                    <div class="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full border border-base-content/10 bg-base-content/10 text-[13px] font-semibold text-base-content/70">
                      {initials}
                    </div>
                    <div class="min-w-0">
                      <div class="whitespace-normal truncate text-[13px] font-medium text-accent/90">
                        {props.name}
                      </div>
                      <div class="truncate text-[11px] text-base-content/40">
                        {props.contextName}
                      </div>
                    </div>
                  </div>
                </li>
                {props.showHeaderDivider && (
                  <div class="mx-2 my-1 h-px bg-base-content/10" />
                )}
                {props.renderFooter ? (
                  <Fragment>
                    <div class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                      {props.renderItems()}
                    </div>
                    <div class="flex-shrink-0 bg-base-300">
                      {props.renderFooter()}
                    </div>
                  </Fragment>
                ) : (
                  props.renderItems()
                )}
              </Fragment>
            ),
          }}
        </Dropdown>
      );
    };
  },
});
